/*
 *
 * MerchantPayByLinkDetail
 *
 * @version 1.0.0
 * @author Giorgia Cuozzo
 * @since 20-Nov-2024
 */

import React from 'react'
import PropTypes from 'prop-types'
import { Capacitor } from '@capacitor/core'
import { Share } from '@capacitor/share'
import { useMobileDetect } from 'Hoc/ListenerProvider'
import Toggle from 'Hoc/ToggleComponent'
import Dialog from '@mui/material/Dialog'
import MerchantTableBadge from '@nexidigital/merchanttablebadge'
import MerchantSocialSharing from '@nexidigital/merchantsocialsharing'
import PortalButton from '@nexidigital/portalbutton'
import { Copy, Dismiss, Mail, SocialWhatsapp, SocialTelegram } from '@nexidigital/iconlibrary'
import DataRow from 'Components/DataRow'
import FlowTemplate from 'Components/Template/FlowTemplate'
import { useIntl } from 'react-intl'
import formattedMessage from '../../modules/languageModule'
import { flowToastMapType } from 'Utility/flowToastMap'
import { mapTransactionStatusesToLabelAndColor, transactionStatusesMapType } from 'Route/PayByLink/modules/transactionStatusesMapper'
import { formatAmountString, formatDateAndHourByIso } from 'Utility/globalMethods'
import { useNavigate } from 'react-router-dom'
import { capitalizeAllString } from 'Utility/utils'
import './MerchantPayByLinkDetail.scss'

const MerchantPayByLinkDetail = (props) => {
  const { isSmallScreen } = useMobileDetect()
  const { code, linkId, shouldSendLinkId, orderDateTime, expirationDate, clientName, paymentDescription, paymentDate, shop, terminalCode, typology, amount, status, buttonLabels } = props

  const intl = useIntl()
  const navigate = useNavigate()

  const statusDetails = mapTransactionStatusesToLabelAndColor(status, intl)

  const buttonLabelMap = {
    PAID: buttonLabels[transactionStatusesMapType.PAID],
    EXPIRED: buttonLabels[transactionStatusesMapType.EXPIRED],
    FAILED: buttonLabels[transactionStatusesMapType.FAILED],
    EXPIRED_FAILED: buttonLabels[transactionStatusesMapType.EXPIRED_FAILED],
    NOT_PAID: isSmallScreen ? formattedMessage.shareLink : ''
  }

  const primaryButtonLabel = buttonLabelMap[status] || ''

  const handleButtonClick = () => {
    const formatDate = (date) => {
      date = date.split(' - ')[0]

      const [day, month, year] = date.split('/')
      return `${year}-${month}-${day}`
    }

    const formattedDate = formatDate(orderDateTime)

    const payload = {
      copyFromCode: code,
      createdDate: formattedDate,
      shopId: shop.id,
      terminalCode
    }

    if (shouldSendLinkId && linkId) {
      payload.linkId = linkId
    }

    navigate('/pay-by-link/require-pbl', {
      state: {
        pblCreationPayload: payload
      }
    })
    props.actions.doHidePopup()
  }

  const handleCopy = (link) => {
    navigator.clipboard.writeText(link)
      .then(() => {
        props.actions.doShowToast(flowToastMapType.SUCCESS_TOAST, {
          label: formattedMessage.copySuccess
        })
      })
      .catch((err) => console.error('Failed to copy:', err))
  }

  const shareLinkApp = async () => {
    const objShareLink = {
      title: intl.formatMessage(
        {
          id: 't.app.paybylinkoutcome.socialsharing.text.subject'
        },
        {
          shopName: props.linkInfo.shopName
        }
      ),
      text: intl.formatMessage(
        {
          id: 't.app.paybylinkoutcome.socialsharing.text'
        },
        {
          shopName: props.linkInfo.shopName,
          transactionAmount: formatAmountString(props.linkInfo.transactionAmount),
          description: props.linkInfo.description,
          expirationDateTime: formatDateAndHourByIso(props.linkInfo.expirationDateTime),
          vatNumber: props.vatNumber !== '' ? props.vatNumber : props.fiscalCode,
          shopAddress: props.linkInfo.shopAddress,
          shopPhoneNumber: props.linkInfo.shopPhoneNumber,
          link: props.link
        }
      )
      // url: encodeURIComponent(props.link)
    }

    await Share.share(objShareLink)
  }

  const shareText = () => intl.formatMessage(
    {
      id: 't.app.paybylinkoutcome.socialsharing.text'
    },
    {
      shopName: props.linkInfo.shopName,
      transactionAmount: formatAmountString(props.linkInfo.transactionAmount),
      description: props.linkInfo.description,
      expirationDateTime: formatDateAndHourByIso(props.linkInfo.expirationDateTime),
      vatNumber: props.vatNumber !== '' ? props.vatNumber : props.fiscalCode,
      shopAddress: props.linkInfo.shopAddress,
      shopPhoneNumber: props.linkInfo.shopPhoneNumber,
      link: props.link
    }
  )

  const shareSubject = () => intl.formatMessage(
    {
      id: 't.app.paybylinkoutcome.socialsharing.text.subject'
    },
    {
      shopName: props.linkInfo.shopName
    }
  )

  const socialShareMessage = () => {
    const message = shareText()
    return encodeURIComponent(message)
  }

  const socialShareSubject = () => {
    const message = shareSubject()
    return encodeURIComponent(message)
  }

  console.log(props.storeOptions)

  return (
    <Toggle>
      <Dialog fullScreen open className='pblDetailDialog'>
        <FlowTemplate
          flowBackground
          titleNavigation={formattedMessage.transactionDetailNavTitle}
          iconCloseRight={<Dismiss size={isSmallScreen ? '24' : '20'} type='line' />}
          labelButtonRightNav={formattedMessage.close}
          onClickClose={() => props.actions.doHidePopup()}
          emptyPage
        >
          <sector className='pblLinkDetail'>
            <h1 className='pblLinkDetailTitle'>
              {formattedMessage.transactionDetailOrderDetailTitle(code)}
            </h1>
            <div className='pblLinkDetailContainer'>
              <div className='pblLinkDetailDatas'>
                <DataRow
                  label={formattedMessage.transactionDetailOrderNumber}
                  content={code}
                />
                <DataRow
                  label={formattedMessage.transactionDetailOrderDateTime}
                  content={orderDateTime}
                />
                <DataRow
                  label={formattedMessage.transactionDetailFacility}
                  content={
                    <MerchantTableBadge
                      label={capitalizeAllString(shop.label)}
                      status='neutralLight'
                    />
                  }
                />
                <DataRow
                  label={formattedMessage.transactionDetailTerminal}
                  content={terminalCode}
                />
                <DataRow
                  label={formattedMessage.transactionDetailTypology}
                  content={<MerchantTableBadge label={typology} status='brand' />}
                />
                <DataRow
                  label={formattedMessage.transactionDetailExpirationDate}
                  content={expirationDate}
                />
                <DataRow
                  label={formattedMessage.transactionDetailAmount}
                  content={amount}
                />
                <DataRow
                  label={formattedMessage.transactionDetailStatus}
                  content={<MerchantTableBadge symbol label={statusDetails.label} status={statusDetails.color} />}
                />
                {
                  status === transactionStatusesMapType.PAID &&
                    <DataRow label={formattedMessage.transactionDetailPaymentDate} content={paymentDate} />
                }
                {
                  status === transactionStatusesMapType.FAILED &&
                    <DataRow label={formattedMessage.transactionDetailStatusDetail} content={formattedMessage.transactionFailedDetailStatusDetail} />
                }
                <DataRow label={formattedMessage.transactionDetailClientName} content={clientName} />
              </div>
              <div className='pblLinkDetailCausal'>
                <DataRow full label={formattedMessage.transactionDetailReason} content={paymentDescription} />
              </div>
            </div>
            {status === transactionStatusesMapType.NOT_PAID
              ? (
                <div className='statusContainer'>

                  {Capacitor.isNativePlatform()
                    ? (
                      <PortalButton
                        label={formattedMessage.shareLink}
                        onClick={() => shareLinkApp()}
                        secondary
                      />
                      )
                    : <MerchantSocialSharing
                        title={formattedMessage.socialSharingTitle}
                        socialChannels={[
                          {
                            name: formattedMessage.socialSharingCopy,
                            icon: <Copy size='24' type='line' />,
                            onClick: () => handleCopy(props.link)
                          },
                          {
                            name: formattedMessage.socialSharingEmail,
                            icon: <Mail size='24' type='line' />,
                            onClick: () => window.open(`mailto:?subject=${socialShareSubject()}&body=${socialShareMessage()}`, '_blank')
                          },
                          {
                            name: formattedMessage.socialSharingWhatsApp,
                            icon: <SocialWhatsapp size='24' type='line' />,
                            onClick: () => window.open(`https://wa.me/?text=${socialShareMessage()}`, '_blank')
                          },
                          {
                            name: formattedMessage.socialSharingTelegram,
                            icon: <SocialTelegram size='24' type='line' />,
                            onClick: () => window.open(`https://t.me/share/url?url=${socialShareMessage()}`, '_blank')
                          }
                        ]}
                        withoutBg
                      />}
                </div>
                )
              : (
                <div className='statusButtonContainer'>
                  <PortalButton
                    label={primaryButtonLabel}
                    onClick={handleButtonClick}
                    loading={props.isLoading}
                    secondary
                  />
                </div>
                )}
          </sector>
        </FlowTemplate>
      </Dialog>
    </Toggle>
  )
}

MerchantPayByLinkDetail.propTypes = {
  // Add here some propTypes
  actions: PropTypes.object,
  /** This props is expected to be a boolean. It is used to set the loading */
  isLoading: PropTypes.bool,
  /** This props is expected to be a boolean. It is used to open the modal */
  open: PropTypes.bool,
  /** This props is expected to be a string. It is used to set the title of the top bar */
  topBarTitle: PropTypes.string,
  /** This props is expected to be a function. It is used to close the modal */
  onClose: PropTypes.func,
  /** This props is expected to be a string. It is used to set the order number */
  code: PropTypes.string,
  linkId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldSendLinkId: PropTypes.bool,
  /** This props is expected to be a string. It is used to set the date and time */
  orderDateTime: PropTypes.string,
  /** This props is expected to be a string. It is used to set the establishment */
  shop: PropTypes.shape({
    id: PropTypes.string,
    code: PropTypes.string,
    label: PropTypes.string
  }),
  /** This props is expected to be a string. It is used to set the terminalCode */
  terminalCode: PropTypes.string,
  /** This props is expected to be a string. It is used to set the type */
  typology: PropTypes.string,
  /** This props is expected to be a string. It is used to set the link expiration */
  expirationDate: PropTypes.string,
  /** This props is expected to be a string. It is used to set the amount */
  amount: PropTypes.string,
  /** This props is expected to be a string. It is used to set the status */
  status: PropTypes.oneOf(['NOT_PAID', 'PAID', 'FAILED', 'EXPIRED', 'EXPIRED_FAILED']),
  /** This props is expected to be a string. It is used to set the customer name */
  clientName: PropTypes.string,
  /** This props is expected to be a string. It is used to set the reason */
  paymentDescription: PropTypes.string,
  /** This props is expected to be an array. It is used to set the social channels */
  socialChannels: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      icon: PropTypes.string
    })
  ),
  /** This props is expected to be an object. It is used to set the button labels */
  buttonLabels: PropTypes.shape({
    pagato: PropTypes.string,
    nonPagato: PropTypes.string
  }),
  /** This props is expected to be a string. It is used to set the payment date */
  paymentDate: PropTypes.string,
  /** This props is expected to be a string. It is used to set the link */
  link: PropTypes.string,
  storeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      code: PropTypes.string,
      label: PropTypes.string,
      terminalCode: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  linkInfo: PropTypes.shape({
    shopName: PropTypes.string,
    transactionAmount: PropTypes.string,
    description: PropTypes.string,
    expirationDateTime: PropTypes.string,
    shopAddress: PropTypes.string,
    shopPhoneNumber: PropTypes.string
  }),
  vatNumber: PropTypes.string.isRequired,
  fiscalCode: PropTypes.string.isRequired
}

MerchantPayByLinkDetail.defaultProps = {
  // Add here some default propTypes values
  actions: {},
  isLoading: false,
  topBarTitle: '',
  onClose: () => { },
  socialChannels: [
    { name: 'Copia', icon: 'copy' },
    { name: 'Email', icon: 'email' },
    { name: 'WhatsApp', icon: 'whatsapp' },
    { name: 'Telegram', icon: 'telegram' }
  ],
  buttonLabels: {
    pagato: 'Richiedi Nuovo Pagamento',
    nonPagato: 'Richiedi Nuovo Link'
  },
  code: '',
  shouldSendLinkId: false,
  orderDateTime: '',
  expirationDate: '',
  clientName: '',
  paymentDescription: '',
  paymentDate: '',
  amount: '',
  status: '',
  shop: {},
  terminalCode: '',
  typology: '',
  link: '',
  linkInfo: {
    shopName: '',
    transactionAmount: '',
    description: '',
    expirationDateTime: '',
    shopAddress: '',
    shopPhoneNumber: ''
  },
  storeOptions: [],
  vatNumber: '',
  fiscalCode: ''
}

export default MerchantPayByLinkDetail
